import type {PDFPageProxy} from 'pdfjs-dist/types/src/display/api';
import type {IAugmentedJQuery, IDoCheck, IPostLink} from 'angular';
import type {Component, OnChanges, OnChangesObject} from 'typings/portaro.fe.types';

export abstract class KpPdfScrollingPageComponentController implements KpPdfScrollingPageComponentBindings, IPostLink, OnChanges<KpPdfScrollingPageComponentBindings>, IDoCheck {
    public page: PDFPageProxy;
    public focusedPage: number;
    public scrollTop = 0;
    public clientHeight = 0;
    public enablePageJump = false;

    public abstract onPageJump(locals: { $elementTop: number });

    private elementTop = 0;
    private elementHeight = 0;
    private isPageFocusedOld = false;

    /*@ngInject*/
    constructor(private $element: IAugmentedJQuery) {
    }

    public $postLink(): void {
        this.updateElementBounds();
    }

    public $onChanges(onChangesObj: OnChangesObject<KpPdfScrollingPageComponentBindings>): void {
        if (onChangesObj.scrollTop && onChangesObj.scrollTop.currentValue ||
            onChangesObj.clientHeight && onChangesObj.clientHeight.currentValue) {
            this.updateElementBounds();
        }
    }

    public $doCheck(): void {
        if (typeof this.isPageFocused === 'boolean' && this.isPageFocused !== this.isPageFocusedOld) {
            this.isPageFocusedOld = this.isPageFocused;

            this.jumpToPage();
        }
    }

    public updateElementBounds() {
        const {offsetTop, offsetHeight} = this.$element[0];
        this.elementTop = offsetTop;
        this.elementHeight = offsetHeight;
    }

    private jumpToPage() {
        if (!this.enablePageJump || this.isElementFocused || !this.isPageFocused) {
            return;
        }

        this.updateElementBounds();

        this.onPageJump({$elementTop: this.elementTop});
    }

    get isPageFocused(): boolean {
        return this.page.pageNumber === this.focusedPage;
    }

    get isElementFocused(): boolean {
        const {elementTop, bottom, elementHeight, scrollTop, clientHeight} = this;
        if (!elementHeight) {
            return;
        }

        const halfHeight = (elementHeight / 2);
        const halfScreen = (clientHeight / 2);
        const delta = elementHeight >= halfScreen ? halfScreen : halfHeight;
        const threshold = scrollTop + delta;

        return elementTop < threshold && bottom >= threshold;
    }

    get bottom(): number {
        return this.elementTop + this.elementHeight;
    }

    get scrollBottom(): number {
        return this.scrollTop + this.clientHeight;
    }
}

export default class KpPdfScrollingPageComponent implements Component<KpPdfScrollingPageComponentBindings> {
    public static componentName = 'kpScrollingPage';

    public bindings = {
        page: '<',
        focusedPage: '<?',
        scrollTop: '<?',
        clientHeight: '<?',
        enablePageJump: '<?',
        onPageJump: '&'
    } as const;

    public controller = KpPdfScrollingPageComponentController;
    public controllerAs = 'pdfScrollingPageController';
}

interface KpPdfScrollingPageComponentBindings {
    page: PDFPageProxy;
    focusedPage?: number;
    scrollTop?: number;
    clientHeight?: number;
    enablePageJump?: boolean;

    onPageJump(locals: { $elementTop: number });
}
